import { useMutation } from '@tanstack/react-query'
import {
  App,
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Tooltip,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  permissionsCreateMutation,
  permissionsDestroyMutation,
  permissionsListQueryKey,
  permissionsPartialUpdateMutation,
  type OrganizationRoleFull,
  type PermissionsLocalized
} from 'src/api/main'
import { ProTableContainer, TableActionColumnTitle } from 'src/components'
import { useDebouncedValue, useInvalidatePredicateQueries } from 'src/hooks'
import {
  readLocalized,
  toBaseLang,
  useShowApiErrors,
  type LocalizedField
} from 'src/utils'
import {
  PERMISSION_CODE_PATTERN,
  slugifyPermissionCode
} from 'src/utils/string'

import { permissionMatchesQuery, type LocalizedPermission } from './utils'

const { Text } = Typography

const CATALOG_PAGE_SIZE = 25

type Props = {
  applicationId: number
  permissions: PermissionsLocalized[]
  roles: OrganizationRoleFull[]
}

type FormValues = {
  code: string
  label: string
  description: string
}

export const PermissionsCatalogPanel = ({
  applicationId,
  permissions,
  roles
}: Props) => {
  const { t, i18n } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<LocalizedPermission | null>(null)
  const [form] = Form.useForm<FormValues>()
  const lang = toBaseLang(i18n.language)

  const [search, setSearch] = useState('')
  const debounced = useDebouncedValue(search.trim().toLowerCase(), 200)

  const labelWatch = Form.useWatch('label', form)

  // Once the user edits the code by hand we stop deriving it from the label.
  const codeTouchedRef = useRef(false)

  useEffect(() => {
    if (!open) return
    codeTouchedRef.current = false
    if (editing) {
      form.setFieldsValue({
        code: editing.code,
        label: readLocalized(editing.label, lang),
        description: readLocalized(editing.description, lang)
      })
      return
    }
    form.resetFields()
  }, [editing, form, lang, open])

  // Auto-fill the code from the label for new permissions, until the user
  // overrides it. Code is immutable when editing, so never touch it there.
  useEffect(() => {
    if (!open || editing || codeTouchedRef.current) return
    form.setFieldValue('code', slugifyPermissionCode(labelWatch ?? ''))
  }, [open, editing, labelWatch, form])

  const { mutateAsync: createPermission, isPending: isCreating } = useMutation({
    ...permissionsCreateMutation()
  })
  const { mutateAsync: updatePermission, isPending: isUpdating } = useMutation({
    ...permissionsPartialUpdateMutation()
  })
  const { mutateAsync: destroyPermission } = useMutation({
    ...permissionsDestroyMutation()
  })

  const countRolesUsing = (permissionId: number) =>
    roles.filter((role) => {
      if (!Array.isArray(role.permissions)) return false

      return role.permissions.some(
        (p) => (p as { id?: number })?.id === permissionId
      )
    }).length

  const visiblePermissions = useMemo(
    () => permissions.filter((p) => permissionMatchesQuery(p, debounced, lang)),
    [debounced, lang, permissions]
  )

  const handleAdd = () => {
    setEditing(null)
    setOpen(true)
  }

  const handleEdit = (record: PermissionsLocalized) => {
    setEditing(record as LocalizedPermission)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setEditing(null)
  }

  const handleDelete = async (record: PermissionsLocalized) => {
    try {
      await destroyPermission({ path: { id: record.id } })
      await invalidateQueries([permissionsListQueryKey()])
      message.success(t('workspace.apps.roles.permissionDeleted'))
    } catch (error) {
      showApiErrors(error)
    }
  }

  const handleSubmit = async (values: FormValues) => {
    const labelPayload = { [lang]: values.label }
    const descriptionPayload = { [lang]: values.description ?? '' }

    try {
      if (editing) {
        await updatePermission({
          path: { id: editing.id },
          body: {
            label: labelPayload,
            description: descriptionPayload
          }
        })
        message.success(t('workspace.apps.roles.permissionUpdated'))
      } else {
        await createPermission({
          body: {
            application: applicationId,
            code: values.code,
            label: labelPayload,
            description: descriptionPayload
          }
        })
        message.success(t('workspace.apps.roles.permissionCreated'))
      }
      await invalidateQueries([permissionsListQueryKey()])
      handleClose()
    } catch (error) {
      showApiErrors(error, form)
    }
  }

  const renderLocalized = (value: LocalizedField) => {
    const text = readLocalized(value, lang)

    return text || <Text type="secondary">—</Text>
  }

  const columns: ColumnsType<PermissionsLocalized> = [
    {
      title: t('workspace.apps.roles.permissionLabel'),
      dataIndex: 'label',
      key: 'label',
      fixed: 'left',
      render: renderLocalized
    },
    {
      title: t('workspace.apps.roles.permissionCode'),
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => <code>{code}</code>
    },
    {
      title: t('workspace.apps.roles.permissionDescription'),
      dataIndex: 'description',
      key: 'description',
      render: renderLocalized
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 96,
      render: (_, record) => {
        const usedBy = countRolesUsing(record.id)

        return (
          <Space size={0}>
            <Tooltip title={t('edit')}>
              <Button
                type="text"
                size="small"
                icon={<Pencil size={14} />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
            <Popconfirm
              title={t('workspace.apps.roles.deletePermissionTitle')}
              description={t('workspace.apps.roles.deletePermissionWarning', {
                count: usedBy
              })}
              okText={t('delete')}
              cancelText={t('cancel')}
              okButtonProps={{ danger: true }}
              onConfirm={() => void handleDelete(record)}
            >
              <Tooltip title={t('delete')}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<Trash2 size={14} />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      }
    }
  ]

  return (
    <>
      <ProTableContainer<PermissionsLocalized>
        showSearch={false}
        rowKey="id"
        bordered
        columns={columns}
        dataSource={visiblePermissions}
        pagination={{
          pageSize: CATALOG_PAGE_SIZE,
          hideOnSinglePage: true,
          showSizeChanger: false
        }}
        leftChildren={
          <Space vertical size={2}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {t('workspace.apps.roles.permissionsCatalogTitle')}
            </Typography.Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.apps.roles.permissionsCatalogDescription')}
            </Text>
          </Space>
        }
        rightChildren={
          <Flex gap={8}>
            <Input
              size="small"
              prefix={<Search size={14} />}
              placeholder={t('workspace.apps.roles.searchPermissions')}
              style={{ width: 220 }}
              variant="filled"
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button icon={<Plus size={16} />} onClick={handleAdd}>
              {t('workspace.apps.roles.addPermission')}
            </Button>
          </Flex>
        }
      />

      <Modal
        open={open}
        onCancel={handleClose}
        title={
          editing
            ? t('workspace.apps.roles.editPermission')
            : t('workspace.apps.roles.addPermission')
        }
        onOk={() => form.submit()}
        okText={t('save')}
        cancelText={t('cancel')}
        confirmLoading={isCreating || isUpdating}
        destroyOnHidden
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => void handleSubmit(values)}
        >
          <Form.Item
            name="label"
            label={t('workspace.apps.roles.permissionLabel')}
            rules={[
              {
                required: true,
                message: t('workspace.apps.roles.permissionLabelRequired')
              }
            ]}
          >
            <Input
              placeholder={t('workspace.apps.roles.permissionLabelPlaceholder')}
            />
          </Form.Item>
          <Form.Item
            name="code"
            label={t('workspace.apps.roles.permissionCode')}
            extra={
              editing
                ? t('workspace.apps.roles.permissionCodeImmutable')
                : t('workspace.apps.roles.permissionCodeHelp')
            }
            rules={[
              {
                required: !editing,
                message: t('workspace.apps.roles.permissionCodeRequired')
              },
              {
                pattern: PERMISSION_CODE_PATTERN,
                message: t('workspace.apps.roles.permissionCodeInvalid')
              }
            ]}
          >
            <Input
              placeholder={t('workspace.apps.roles.permissionCodePlaceholder')}
              disabled={Boolean(editing)}
              onChange={() => {
                codeTouchedRef.current = true
              }}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label={t('workspace.apps.roles.permissionDescription')}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
